import Raven from 'raven-js';

import { ObjectTypeId } from 'customer-data-objects/constants/ObjectTypeIds';

import {
  SmartPropertyStatus,
  SmartPropertyStatusMetadata,
  getSmartPropertyBatchRpc,
} from '../../__generated__/chirp/com/hubspot/custom/enrichment/rpc/client/SmartPropertyStatusService';
import { useRpcClient } from '../client/useRpcClient';
import { getOwnProperty } from '../utils/getOwnProperty';
import { AIProcessingStatus } from './AIPropertiesProcessingContextProvider';
import { makeIdentifierKey, parseIdentifierKey } from './identifierKey';

export const NO_STATUS = Symbol('NO_STATUS');

export type SmartPropertyPollResult = {
  metadata: SmartPropertyStatusMetadata;
  smartPropertyResultId?: number;
  status: AIProcessingStatus | typeof NO_STATUS;
};

function mapStatus(
  status: SmartPropertyStatus
): AIProcessingStatus | typeof NO_STATUS {
  switch (status) {
    case 'RUNNING':
      return 'pending';
    case 'COMPLETE_SUCCESS':
      return 'completed';
    case 'COMPLETE_NO_RESULT':
      return 'completedNoResult';
    case 'FAILURE':
      return 'failed';
    default:
      return NO_STATUS;
  }
}

interface RequestData {
  propertyNames: Set<string>;
  objectIds: Set<number>;
}

function collateRequestsByObjectTypeId(
  identifierKeys: string[]
): [ObjectTypeId, RequestData][] {
  const requests: Record<ObjectTypeId, RequestData> = {};
  for (const key of identifierKeys) {
    const { objectTypeId, propertyName, objectId } = parseIdentifierKey(key);
    const request = getOwnProperty(requests, objectTypeId) ?? {
      propertyNames: new Set(),
      objectIds: new Set(),
    };
    request.propertyNames.add(propertyName);
    request.objectIds.add(parseInt(objectId, 10));
    requests[objectTypeId] = request;
  }
  return Object.entries(requests);
}

export async function pollSmartPropertyStatusesWithMetadata(
  rpcClient: ReturnType<typeof useRpcClient>,
  identifierKeys: string[]
): Promise<Record<string, SmartPropertyPollResult>> {
  const responses = await Promise.all(
    collateRequestsByObjectTypeId(identifierKeys).map(
      ([objectTypeId, request]) =>
        rpcClient
          .call(getSmartPropertyBatchRpc, {
            request: {
              objectTypeId,
              propertyNames: Array.from(request.propertyNames),
              objectIds: Array.from(request.objectIds),
            },
          })
          .then((result) => {
            const statusesWithMetadata: Record<
              string,
              SmartPropertyPollResult
            > = {};
            for (const [objectId, statusesByPropertyName] of Object.entries(
              result.responses
            )) {
              for (const [
                propertyName,
                { metadata, smartPropertyResultId, status },
              ] of Object.entries(statusesByPropertyName)) {
                const key = makeIdentifierKey(
                  objectTypeId,
                  propertyName,
                  objectId
                );
                statusesWithMetadata[key] = {
                  status: mapStatus(status),
                  metadata,
                  smartPropertyResultId,
                };
              }
            }

            return statusesWithMetadata;
          })
          .catch((e) => {
            Raven.captureException(
              new Error('Error polling smart property statuses'),
              { extra: { e } }
            );
            return {};
          })
    )
  );

  const statusesWithMetadata: Record<string, SmartPropertyPollResult> = {};
  for (const response of responses) {
    for (const [key, value] of Object.entries(response)) {
      statusesWithMetadata[key] = value;
    }
  }

  // Every key that was requested and not returned by the backend should be returned with NO_STATUS
  for (const key of identifierKeys) {
    if (!Object.prototype.hasOwnProperty.call(statusesWithMetadata, key)) {
      statusesWithMetadata[key] = { status: NO_STATUS, metadata: {} };
    }
  }
  return statusesWithMetadata;
}
